import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { GoogleButton } from "../components/GoogleButton";
import { FacebookButton } from "../components/FacebookButton";
import { track } from "../lib/analytics";
import { useSession } from "../state";
import { AuthShell, Divider } from "./Login";

export function Signup() {
  const { signUp, confirmSignUp, resendConfirmation, signIn } = useSession();
  const nav = useNavigate();

  const [stage, setStage] = useState<"form" | "confirm">("form");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const onSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (cleaned.split("@", 1)[0].includes("+")) {
      setErr("please use your primary email — addresses with “+” aren’t allowed");
      return;
    }
    setBusy(true); setErr(null);
    try {
      await signUp(cleaned, password);
      setStage("confirm");
      setInfo("we sent a 6-digit code to your inbox.");
    } catch (e: any) {
      setErr(humanize(e?.message || e?.code || "couldn't create account"));
    } finally { setBusy(false); }
  };

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await confirmSignUp(email.trim(), code.trim());
      await signIn(email.trim(), password);
      track("signup_complete", { method: "password" });
      nav("/", { replace: true });
    } catch (e: any) {
      setErr(humanize(e?.message || e?.code || "code didn't match"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true); setErr(null);
    try {
      await resendConfirmation(email.trim());
      setInfo("new code sent.");
    } catch (e: any) {
      setErr(humanize(e?.message || ""));
    } finally { setBusy(false); }
  };

  if (stage === "confirm") {
    return (
      <AuthShell>
        <h1 className="font-display font-black text-3xl mb-1">check your email</h1>
        <p className="text-ink-soft mb-6 text-sm">{info ?? "we sent you a 6-digit code."}</p>
        <form onSubmit={onConfirm} className="space-y-3">
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            maxLength={6}
            className="input text-center tracking-[0.4em] text-lg"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <button type="submit" disabled={busy || code.length < 6} className="btn-primary w-full">
            {busy ? "verifying…" : "verify and sign in"}
          </button>
          {err && <p className="text-sm text-coral-dark">{err}</p>}
        </form>
        <div className="mt-6 text-sm text-ink-soft flex justify-between">
          <button onClick={resend} disabled={busy} className="hover:text-ink underline">resend code</button>
          <button onClick={() => setStage("form")} className="hover:text-ink underline">use a different email</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display font-black text-3xl mb-6">create account</h1>

      <div className="space-y-2">
        <GoogleButton label="sign up with google" />
        <FacebookButton label="sign up with facebook" />
      </div>
      <Divider />

      <form onSubmit={onSignUp} className="space-y-3">
        <label className="block">
          <span className="text-xs text-ink-soft">email</span>
          <input type="email" autoComplete="email" required className="input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-ink-soft">password</span>
          <input type="password" autoComplete="new-password" required className="input mt-1" minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />
          <span className="text-[11px] text-ink-mute">10+ characters, with upper, lower, and a number</span>
        </label>
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? "creating…" : "create account"}
        </button>
        {err && <p className="text-sm text-coral-dark">{err}</p>}
      </form>

      <p className="mt-6 text-sm text-ink-soft text-center">
        already have an account? <Link to="/login" className="underline hover:text-ink">sign in</Link>
      </p>
    </AuthShell>
  );
}

function humanize(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("usernameexistsexception") || m.includes("user already exists")) return "an account with that email already exists";
  if (m.includes("invalidpassword") || m.includes("password did not conform")) return "password doesn't meet the requirements (10+ chars, mixed case, number)";
  if (m.includes("invalidparameter")) return "please check your email and password";
  if (m.includes("codemismatch")) return "wrong code — try again";
  if (m.includes("expiredcode")) return "code expired — request a new one";
  if (m.includes("limitexceeded")) return "too many attempts — wait a minute and retry";
  return msg;
}
